import React, {RefObject, useEffect} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import classNames from 'classnames';
import {CoreDoms, ViewMode, EditorExperienceType} from '@taopix/taopix-core';
import {PageInterface, ProductLayout} from '@taopix/taopix-core/dist/types/types';
import {PagesSelector} from '../../../Selectors';
import Preview from '../../Preview/Preview';
import {useUiState} from '../../Context/UserInterface/UserInterfaceContext';
import {UserInterfaceActions} from '../../Context/UserInterface/UserInterfaceActionTypes';
import {useExperience} from '../../Experience/ExperienceProvider';
import {useTaopix} from '../../Context/TaopixContext';
import {Button, EditBoxIcon} from '@taopix/taopix-design-system';
import PageName from '../../Page/PageName';
import useViewMode from '../../../Hooks/useViewMode';
import {GridSizes} from '../../GridSizeSelector/GridSizeSelector';
import useKeyboardMode from '../../../Hooks/useKeyboardMode';

export interface PrintPreviewViewProps {
  layout: ProductLayout;
  containerRef: RefObject<HTMLElement>;
}

const PrintPreviewView = ({layout, containerRef}: PrintPreviewViewProps) => {
  // get the pages from the store
  const pages = useSelector(PagesSelector);
  const ui = useUiState();
  const {experience} = useExperience();
  const app = useTaopix();
  const [, setViewMode] = useViewMode();
  const {t} = useTranslation();
  const keyboardMode = useKeyboardMode();

  const onEditPage = (page?: PageInterface) => {
    // Set the core to the requested page
    if (page) app.core.get('abilities').setPage(page.pagenumber).then();

    // if grid is the default view, attempt to choose list or single first (as they are better for editing)
    if ('gv' === experience.EDITOR.workArea.defaultView) {
      if (experience.EDITOR.workArea.editingViewModes.list) {
        setViewMode(ViewMode.List);
        return void 0;
      }
      if (experience.EDITOR.workArea.editingViewModes.page) {
        setViewMode(ViewMode.Single);
        return void 0;
      }
      setViewMode(ViewMode.Grid);
    } else {
      // Otherwise choose the default view
      const mode =
        experience.EDITOR.workArea.defaultView === 'ls' ? ViewMode.List : ViewMode.Single;
      setViewMode(mode);
      return void 0;
    }
  };

  // Classes for the UL
  const pagesContainerClasses = classNames(
    'mt-[80rem]',
    ViewMode.List === ui.state.previewViewMode && [
      'after:w-full',
      'after:shrink-0',
      'after:h-[500rem]',
    ],
    ViewMode.Grid === ui.state.previewViewMode && [
      'self-start',
      'mb-[100rem]',
      'lg:px-[20rem]',
      'sm:px-xl',
    ]
  );

  const pageToolbarClasses = classNames(
    'flex',
    'justify-center',
    'h-xl',
    'transition-opacity',
    'space-x-xs',
    !keyboardMode && 'mouse:opacity-0',
    'mouse:group-hover/preview-page:opacity-100'
  );

  const tpxCoreAttrs = {
    [CoreDoms.ProjectPagesAttr]: 'true',
  };

  const listItemClasses = classNames([
    'flex',
    'flex-col',
    'snap-center',
    'group/preview-page',
    ui.state.previewViewMode === ViewMode.List && 'mb-[20rem]',
  ]);

  useEffect(() => {
    ui.dispatch(UserInterfaceActions.setToolPaletteSection(null));
    ui.dispatch(UserInterfaceActions.setFocusMode(false));
  }, []);

  return (
    <ul {...tpxCoreAttrs} className={pagesContainerClasses}>
      {pages.map((page: PageInterface) => {
        return (
          <li data-id={page.pagenumber} className={listItemClasses} key={`preview-${page.ref}`}>
            <Preview
              page={page}
              layout={layout}
              editorType={EditorExperienceType.ReadOnly}
              viewMode={ui.state.previewViewMode}
              gridCellMaxWidth={ui.state.gridSize ?? GridSizes.LARGE}
              gridColGap={20}
              gridRowGap={20}
            >
              <PageName page={page} layout={layout} />
              <div className={pageToolbarClasses}>
                <Button
                  buttonStyle={'standard'}
                  size="small"
                  label={t('*:str_LabelPageEdit')}
                  startIcon={<EditBoxIcon />}
                  labelLines={1}
                  onClick={() => onEditPage(page)}
                />
              </div>
            </Preview>
          </li>
        );
      })}
    </ul>
  );
};

export default PrintPreviewView;
